import {
  Alert,
  Button,
  Card,
  Skeleton,
  Typography
} from 'antd';
import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import CodeInput from '../../UI/input/CodeInput';
import useCountdown, { formatCountdown } from '../../hooks/useCountdown';
import {
  confirmTotp,
  enrolTotp,
  isEnrolmentExpired,
  messageFor,
  serverDetail,
  statusOf,
  type TotpEnrolment
} from '../../../service/mfa.service';
import {
  SESSION_EXPIRED,
  TOTP_ALREADY_SET,
  type FactorEnrolmentProps
} from './mfaErrors';

/**
 * Enrolling the authenticator app: mint a secret, draw the QR, and turn the factor on only
 * once the server has checked a code against it.
 *
 * Lifted out of `TwoFactorPanel` so the Security tab and the blocking enrolment popup drive
 * the same code. It **enrols on mount**, and mounting is the decision to enrol.
 *
 * The secret and its enrolment ticket live in this component's state and nowhere else: not in
 * redux, not in `localStorage`, not in the URL, not in a log line. Unmounting is how they are
 * disposed of, which is why navigating away or closing the popup cancels an enrolment in
 * progress.
 */

const ENROLMENT_TTL_FALLBACK_SECONDS = 600;

const TotpEnrolmentFlow = ({ onEnrolled, onCancel }: FactorEnrolmentProps) => {
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null);
  const [code, setCode] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [isBusy, setIsBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const enrolmentExpiry = useCountdown(0);

  /**
   * Mints a secret and draws a QR. Turns nothing on, and is safe to repeat: each call replaces
   * the previous secret server-side, which is also how an expired enrolment recovers.
   */
  const start = async (freshNotice?: string) => {
    setIsBusy(true);
    setError(null);
    try {
      const { data } = await enrolTotp();
      setEnrolment(data);
      setCode('');
      setShowSecret(false);
      setNotice(freshNotice ?? null);
      enrolmentExpiry.restart(data.expires_in ?? ENROLMENT_TTL_FALLBACK_SECONDS);
    } catch (err: any) {
      setNotice(null);
      if (statusOf(err) === 409) {
        // Keycloak already holds an authenticator for this account and the caller was
        // showing a stale "off". Hand it back rather than offering a second QR.
        onCancel(serverDetail(err) ?? TOTP_ALREADY_SET);
        return;
      }
      setError(
        messageFor(err, {
          401: SESSION_EXPIRED,
          429: 'Too many setup attempts. Please wait a minute and try again.',
          502: 'Cannot reach the identity service right now. Please try again shortly.'
        }, 'Could not start authenticator setup. Please try again.')
      );
    } finally {
      setIsBusy(false);
    }
  };

  // Enrol once, on mount. Guarded against StrictMode's double-invoke, which would otherwise
  // burn one of the route's five-per-minute allowance and replace a QR mid-scan.
  const hasRequested = useRef(false);
  useEffect(() => {
    if (hasRequested.current) return;
    hasRequested.current = true;
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async (submitted: string) => {
    if (!enrolment) return;
    setIsBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { data } = await confirmTotp(enrolment.enrolment_ticket, submitted);
      enrolmentExpiry.stop();
      // Absent whenever the account already holds unused codes. Check for the key.
      onEnrolled(data?.recovery_codes);
    } catch (err: any) {
      // Two different 400s. "Wrong code" keeps this QR and this ticket; "start again" means
      // the server has forgotten the secret behind it, so retyping codes could never succeed.
      // That one gets a fresh QR instead of an error to stare at.
      if (isEnrolmentExpired(err)) {
        const detail = serverDetail(err);
        setEnrolment(null);
        await start(
          `${detail ?? 'That setup expired.'} Here is a fresh QR code. Scan this one instead.`
        );
        return;
      }
      if (statusOf(err) === 409) {
        onCancel(serverDetail(err) ?? TOTP_ALREADY_SET);
        return;
      }
      setCode('');
      setError(
        messageFor(err, {
          400: 'That code is not correct. Check your authenticator app and try again.',
          401: SESSION_EXPIRED,
          422: `Enter the ${enrolment.digits ?? 6} digits from your authenticator app.`,
          429: 'Too many attempts. Please wait a minute and try again.',
          502: 'Could not save the authenticator right now. Please try again.'
        })
      );
    } finally {
      setIsBusy(false);
    }
  };

  // The ticket dies on the server after `expires_in` whether or not this screen notices. Say
  // so and offer the way out, rather than letting someone type codes at a forgotten secret.
  const isStale = !!enrolment && enrolmentExpiry.secondsLeft === 0;
  const digits = enrolment?.digits ?? 6;

  return (
    <Card size="small" className="mt-4">
      <Typography.Text strong className="text-[13px]">
        Scan this with your authenticator app
      </Typography.Text>
      <div>
        <Typography.Text type="secondary" className="text-[13px]">
          Then enter the {digits}-digit code it shows. The authenticator turns on once that
          code is confirmed.
        </Typography.Text>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          className="mt-3"
          message={error}
          closable
          onClose={() => setError(null)}
        />
      )}

      {notice && (
        <Alert
          type="warning"
          showIcon
          className="mt-3"
          message={notice}
          closable
          onClose={() => setNotice(null)}
        />
      )}

      {!enrolment && isBusy && (
        <Skeleton active className="my-4" paragraph={{ rows: 3 }} />
      )}

      {!enrolment && !isBusy && error && (
        <div className="my-4 text-center">
          <Button loading={isBusy} onClick={() => start()}>
            Try again
          </Button>
        </div>
      )}

      {enrolment && (isStale ? (
        <Alert
          type="warning"
          showIcon
          className="my-3"
          message="This QR code has expired"
          description={
            <div className="text-[13px]">
              Setup codes last ten minutes. Get a new one and scan that instead: a code from
              the old one will not be accepted.
              <div className="mt-2">
                <Button size="small" loading={isBusy} onClick={() => start()}>
                  New QR code
                </Button>
              </div>
            </div>
          }
        />
      ) : (
        <>
          <div className="flex justify-center my-4">
            {/* Black on white whatever the app theme is: a QR inverted or tinted to sit on a
                dark surface is a QR some scanners refuse. */}
            <div 
              className="bg-white p-3 rounded-md border border-solid border-[#d9d9d9] leading-none"
            >
              <QRCodeSVG
                value={enrolment.otpauth_uri}
                size={180}
                level="M"
                marginSize={4}
                bgColor="#ffffff"
                fgColor="#000000"
                title={`Authenticator setup for ${enrolment.account}`}
              />
            </div>
          </div>

          <div className="text-center">
            <Typography.Text 
              type="secondary" 
              className="text-[13px] break-all"
            >
              {enrolment.issuer} · {enrolment.account}
              {enrolmentExpiry.isRunning && (
                <> · expires in {formatCountdown(enrolmentExpiry.secondsLeft)}</>
              )}
            </Typography.Text>
          </div>

          <div className="text-center">
            <Button
              type="link"
              size="small"
              className="!px-0 !text-[13px]"
              onClick={() => setShowSecret((shown) => !shown)}
            >
              {showSecret ? 'Hide the setup key' : 'Cannot scan the code?'}
            </Button>
          </div>

          {showSecret && (
            <div className="text-center">
              <Typography.Paragraph 
                type="secondary" 
                className="!mb-1 text-[13px]"
              >
                Enter this key by hand instead, with time-based (TOTP) selected.
              </Typography.Paragraph>
              <Typography.Text
                code
                copyable={{ text: enrolment.secret }}
                className="!text-[12px] break-all"
              >
                {enrolment.secret}
              </Typography.Text>
              <div>
                <Typography.Text type="secondary" className="text-[12px]">
                  {digits} digits, changing every {enrolment.period} seconds.
                </Typography.Text>
              </div>
            </div>
          )}

          <div className="flex justify-center my-4">
            <CodeInput
              value={code}
              onChange={setCode}
              onComplete={confirm}
              length={digits}
              disabled={isBusy}
              autoFocus
              status={error ? 'error' : undefined}
            />
          </div>
        </>
      ))}

      <div className="flex items-center justify-between gap-2">
        <Button type="link" className="!px-0" onClick={() => onCancel()}>
          Cancel
        </Button>
        {enrolment && !isStale && (
          <Button
            type="primary"
            loading={isBusy}
            disabled={code.length !== digits}
            onClick={() => confirm(code)}
          >
            Confirm
          </Button>
        )}
      </div>
    </Card>
  );
};

export default TotpEnrolmentFlow;
